import React, { useContext, useEffect, useState } from 'react';
import { Transport, TransportFilters } from '../../app/models/Transport';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import useTransportService from '../../app/services/hooks/useTransportService';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import { AppContext } from '../../contexts/AppContext';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import useNavigationPage from '../../hooks/useNavigationPage';
import TransportDataTable from './components/tables/TransportDataTable';
import TransportCreate from './components/TransportCreate';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Transportes',
        url: '/transports',
        isActive: true
    }
];

const Transports = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { fetchingGetTransports, getTransports, deleteTransport } = useTransportService();
    const [transports, setTransports] = useState<Transport[]>([]);
    const { navigationPage } = useNavigationPage();

    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [transportFilter, setTransportFilter] = useState<TransportFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc',
        vehicle_id: null
    });

    useEffect(() => {
        getAllTransports();
    }, [transportFilter.page, transportFilter.per_page, transportFilter.sort, transportFilter.order]);

    const getAllTransports = () => {
        getTransports(transportFilter, {
            onSuccess: (response: ServiceResponse) => {
                setTransports(response.data.transports || []);
                setTotalRows(response.data.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, {
                    autoClose: 2000
                });

                if (response.data?.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (transportId: number) => {
        navigationPage('/transports/' + transportId + '/edit');
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllTransports();
    };

    const destroy = (transportId: number) => {
        const _text = 'Está a punto de eliminar el transporte #' + transportId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando transporte...');
                        deleteTransport(transportId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El transporte no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Transportes" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo Transporte" />}
                />
                
                <TransportDataTable
                    transports={transports}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetTransports}
                    filter={transportFilter}
                    setFilter={setTransportFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate && (
                <>
                    <style dangerouslySetInnerHTML={{__html: `
                        .modal-dialog.transport-modal-height {
                            max-width: 1100px !important;
                            max-height: 90vh !important;
                            margin: 1.75rem auto !important;
                            display: flex !important;
                            flex-direction: column !important;
                        }
                        .modal-dialog.transport-modal-height .modal-content {
                            max-height: 90vh !important;
                            display: flex !important;
                            flex-direction: column !important;
                        }
                        .modal-dialog.transport-modal-height .modal-header {
                            flex-shrink: 0 !important;
                        }
                        .modal-dialog.transport-modal-height .modal-body {
                            flex: 1 1 auto !important;
                            display: flex !important;
                            flex-direction: column !important;
                            overflow: hidden !important;
                            min-height: 0 !important;
                            padding: 1rem !important;
                            padding-bottom: 0.5rem !important;
                        }
                        .transport-modal-height .modal-footer {
                            flex-shrink: 0 !important;
                        }
                        .transport-modal-height .transport-form-container {
                            display: flex !important;
                            flex-direction: column !important;
                            height: 100% !important;
                            min-height: 0 !important;
                            max-height: 100% !important;
                            overflow: hidden !important;
                        }
                        .transport-modal-height .transport-form-container > .row:first-child {
                            flex-shrink: 0 !important;
                            overflow: visible !important;
                        }
                        .transport-modal-height .transport-nodes-section {
                            flex: 1 1 auto !important;
                            display: flex !important;
                            flex-direction: column !important;
                            min-height: 300px !important;
                            max-height: calc(90vh - 380px) !important;
                            overflow: hidden !important;
                            margin-bottom: 0 !important;
                        }
                        .transport-modal-height .transport-nodes-section > .col-12 {
                            display: flex !important;
                            flex-direction: column !important;
                            flex: 1 1 auto !important;
                            min-height: 0 !important;
                            max-height: 100% !important;
                            overflow: hidden !important;
                        }
                        .transport-modal-height .table-responsive.transport-nodes-table {
                            flex: 1 1 auto !important;
                            min-height: 250px !important;
                            max-height: calc(90vh - 430px) !important;
                            overflow-y: auto !important;
                            overflow-x: hidden !important;
                        }
                        .transport-modal-height .table-responsive.transport-nodes-table::-webkit-scrollbar {
                            width: 8px;
                        }
                        .transport-modal-height .table-responsive.transport-nodes-table::-webkit-scrollbar-thumb {
                            background-color: #ccc;
                            border-radius: 4px;
                        }
                        .transport-modal-height .table-responsive.transport-nodes-table::-webkit-scrollbar-thumb:hover {
                            background-color: #999;
                        }
                        .transport-modal-height hr {
                            flex-shrink: 0 !important;
                            margin: 0.75rem 0 0.5rem 0 !important;
                        }
                        .transport-modal-height .row.justify-content-end {
                            flex-shrink: 0 !important;
                            margin-top: 0 !important;
                            margin-bottom: 0 !important;
                            padding-top: 0.5rem !important;
                            padding-bottom: 0 !important;
                        }
                        .transport-modal-height .table tbody tr {
                            position: relative !important;
                        }
                        .transport-modal-height .table tbody tr .react-select__menu {
                            z-index: 9999 !important;
                        }
                        .transport-modal-height .react-select__menu {
                            z-index: 10000 !important;
                        }
                        .transport-modal-height .react-select__menu-portal {
                            z-index: 10000 !important;
                        }
                    `}} />
                    <DefaultModal
                        show={showingCreate}
                        handleClose={hideCreate}
                        title="Crear Transporte"
                        backdrop={true}
                        showFooter={false}
                        dialogClassName="transport-modal-height"
                    >
                        <TransportCreate
                            onSaved={() => {
                                // La navegación a /transports/:id/edit la hace TransportCreate
                                // solo recargamos tabla en background; no cerramos el modal
                                reloadTable();
                            }}
                            onCancel={hideCreate}
                            onError={hideCreate}
                        />
                    </DefaultModal>
                </>
            )}
        </>
    );
};

export default Transports;
